import React from "react";
import { useParams, Link } from "react-router-dom";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Chip from "@material-ui/core/Chip";
import api from "../../api";

const backLinkStyle = { color: "#e6007e" };
const backLinkWrapStyle = { marginBottom: 8 };
const userInfoStyle = { padding: 16, marginBottom: 24 };
const userGridStyle = { gridTemplateColumns: "120px 1fr", gap: "6px 16px" };
const monoStyle = { fontFamily: "monospace" };
const activeChipStyle = { backgroundColor: "#e8f5e9", color: "#2e7d32" };
const expiredChipStyle = { backgroundColor: "#f5f5f5", color: "#757575" };

const OTP_HISTORY_LIMIT = 50;
const otpCapNoteStyle = { marginTop: 8 };
const sendOtpRowStyle = { display: "flex", alignItems: "center", gap: 12, marginBottom: 16 };
const sendErrorStyle = { color: "#d32f2f" };

export default function PartnerUserDetailPage() {
  const { id: partnerId, userId } = useParams();
  const [user, setUser] = React.useState(null);
  const [otps, setOtps] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [userError, setUserError] = React.useState(null);
  const [otpsError, setOtpsError] = React.useState(null);
  const [sending, setSending] = React.useState(false);
  const [sendError, setSendError] = React.useState(null);

  const loadOtps = React.useCallback((signal) => {
    return api.getPartnerUserOtps(userId).then((res) => {
      if (signal?.cancelled) return;
      setOtps(res.data);
      setOtpsError(null);
    }).catch((err) => {
      if (signal?.cancelled) return;
      const status = err?.response?.status;
      setOtpsError(status === 401 ? "Insufficient permissions to view OTP history" : "Failed to load OTP history");
    });
  }, [userId]);

  React.useEffect(() => {
    const signal = { cancelled: false };
    setLoading(true);
    setUserError(null);
    setOtpsError(null);
    setUser(null);
    setOtps([]);

    Promise.allSettled([
      api.getPartnerUser(userId),
      api.getPartnerUserOtps(userId),
    ]).then(([userResult, otpsResult]) => {
      if (signal.cancelled) return;

      if (userResult.status === "fulfilled") {
        setUser(userResult.value.data);
      } else {
        const status = userResult.reason?.response?.status;
        setUserError(status === 404 ? "User not found" : status === 401 ? "Insufficient permissions" : "Failed to load user");
      }

      if (otpsResult.status === "fulfilled") {
        setOtps(otpsResult.value.data);
      } else {
        const status = otpsResult.reason?.response?.status;
        setOtpsError(status === 401 ? "Insufficient permissions to view OTP history" : "Failed to load OTP history");
      }
    }).finally(() => { if (!signal.cancelled) setLoading(false); });

    return () => { signal.cancelled = true; };
  }, [userId]);

  const hasActiveOtp = otps.length > 0 && !otps[0].expired;

  function handleSendOtp() {
    setSending(true);
    setSendError(null);
    api.sendPartnerUserOtp(userId).then(() => {
      loadOtps({});
    }).catch((err) => {
      const status = err?.response?.status;
      if (status === 429) {
        setSendError("An active OTP already exists. Wait for it to expire before sending a new one.");
      } else {
        setSendError("Failed to send OTP. Please try again.");
      }
    }).finally(() => setSending(false));
  }

  if (loading) return <Box p={3}><Typography>Loading...</Typography></Box>;
  if (userError === "User not found") return <Box p={3}><Typography color="error">User not found</Typography></Box>;

  return (
    <Box p={3}>
      <Typography variant="body2" style={backLinkWrapStyle}>
        <Link to={`/partners/${partnerId}`} style={backLinkStyle}>
          ← Back to partner
        </Link>
      </Typography>

      {userError ? (
        <Typography color="error" gutterBottom>{userError}</Typography>
      ) : (
        <>
          <Typography variant="h5" gutterBottom>
            {user.name || user.email}
          </Typography>

          <Paper style={userInfoStyle}>
            <Typography variant="subtitle1" gutterBottom><strong>User Info</strong></Typography>
            <Box display="grid" style={userGridStyle}>
              <Typography variant="body2" color="textSecondary">ID</Typography>
              <Typography variant="body2">{user.id}</Typography>
              <Typography variant="body2" color="textSecondary">Email</Typography>
              <Typography variant="body2">{user.email}</Typography>
              <Typography variant="body2" color="textSecondary">Role</Typography>
              <Typography variant="body2">{user.role}</Typography>
              <Typography variant="body2" color="textSecondary">Created</Typography>
              <Typography variant="body2">{user.createdAt ? new Date(user.createdAt).toLocaleString() : "—"}</Typography>
            </Box>
          </Paper>
        </>
      )}

      <div style={sendOtpRowStyle}>
        <Typography variant="h6">OTP History</Typography>
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={sending || hasActiveOtp}
          title={hasActiveOtp ? "An active OTP already exists" : undefined}
          onClick={handleSendOtp}
        >
          {sending ? "Sending..." : "Send OTP"}
        </Button>
        {hasActiveOtp && (
          <Typography variant="body2" color="textSecondary">Active OTP exists</Typography>
        )}
        {sendError && (
          <Typography variant="body2" style={sendErrorStyle}>{sendError}</Typography>
        )}
      </div>
      {otpsError ? (
        <Typography variant="body2" color="textSecondary">{otpsError}</Typography>
      ) : (
        <>
          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Expires At</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {otps.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body2" color="textSecondary">No OTP history</Typography>
                    </TableCell>
                  </TableRow>
                ) : otps.map((otp) => (
                  <TableRow key={`${otp.code}-${otp.createdAt}`}>
                    <TableCell style={monoStyle}>{otp.code}</TableCell>
                    <TableCell>{otp.createdAt ? new Date(otp.createdAt).toLocaleString() : "—"}</TableCell>
                    <TableCell>{otp.expiresAt ? new Date(otp.expiresAt).toLocaleString() : "—"}</TableCell>
                    <TableCell>
                      <Chip
                        label={otp.expired ? "Expired" : "Active"}
                        size="small"
                        style={otp.expired ? expiredChipStyle : activeChipStyle}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
          {otps.length === OTP_HISTORY_LIMIT && (
            <Typography variant="body2" color="textSecondary" style={otpCapNoteStyle}>
              Showing latest {OTP_HISTORY_LIMIT} records
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}
